import { z } from 'zod';

import type { PendingCartMutation } from './types';

import {
  MUTATION_CART_ADD_LINE_ITEMS_KEY,
  MUTATION_CART_DELETE_LINE_ITEMS_KEY,
  MUTATION_CART_UPDATE_LINE_ITEMS_KEY,
} from '$src/constants';

export const ZCartMutationKey = z.enum([
  MUTATION_CART_ADD_LINE_ITEMS_KEY,
  MUTATION_CART_DELETE_LINE_ITEMS_KEY,
  MUTATION_CART_UPDATE_LINE_ITEMS_KEY,
]);

export const ZAddLineItemsVariables = z.object({
  lineItems: z.array(z.object({ variantId: z.string() })),
});

export const ZUpdateLineItemsVariables = z.object({
  lineItems: z.array(z.object({ id: z.string() })),
});

export const ZDeleteLineItemsVariables = z.object({
  lineItemIds: z.array(z.string()),
});

/**
 * @param mutation - mutation object received from the useMutationState hook
 * @returns zod-validated PendingCartMutation if the received mutation is a valid cart mutation, null otherwise
 */
export function selectPendingMutation(mutation: {
  state: { variables: unknown };
  options: { mutationKey?: readonly unknown[] };
}): PendingCartMutation | null {
  const keyResult = ZCartMutationKey.safeParse(mutation.options.mutationKey?.[0]);
  if (!keyResult.success) return null;

  const vars = mutation.state.variables;

  switch (keyResult.data) {
    case MUTATION_CART_ADD_LINE_ITEMS_KEY: {
      const parsed = ZAddLineItemsVariables.safeParse(vars);
      if (!parsed.success) {
        console.warn(
          '[useCartLineItemsMutatingState] failed to parse `add` line items variables',
          parsed.error,
        );
        return null;
      }
      return { kind: MUTATION_CART_ADD_LINE_ITEMS_KEY, variables: parsed.data };
    }
    case MUTATION_CART_UPDATE_LINE_ITEMS_KEY: {
      const parsed = ZUpdateLineItemsVariables.safeParse(vars);
      if (!parsed.success) {
        console.warn(
          '[useCartLineItemsMutatingState] failed to parse `update` line items variables',
          parsed.error,
        );
        return null;
      }
      return { kind: MUTATION_CART_UPDATE_LINE_ITEMS_KEY, variables: parsed.data };
    }
    case MUTATION_CART_DELETE_LINE_ITEMS_KEY: {
      const parsed = ZDeleteLineItemsVariables.safeParse(vars);
      if (!parsed.success) {
        console.warn(
          '[useCartLineItemsMutatingState] failed to parse `delete` line items variables',
          parsed.error,
        );
        return null;
      }
      return { kind: MUTATION_CART_DELETE_LINE_ITEMS_KEY, variables: parsed.data };
    }
    default:
      return null;
  }
}
